import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { Save, Check, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  fetchSignature,
  updateSignature,
} from "../../../../../redux/actions/inboxAction";
import { backendClient } from "../../../../../api/backend";
import { enqueueSnackbar } from "notistack";

const EmailSettings = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { signature } = useSelector((state) => state.inbox);

  const [html, setHtml] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    dispatch(fetchSignature());
    backendClient.getInboxSyncStatus()
      .then(setSyncStatus)
      .catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (signature) {
      setHtml(signature.signature_html || "");
      setIsActive(signature.is_active ?? true);
    }
  }, [signature]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(updateSignature({ signature_html: html, is_active: isActive }));
      enqueueSnackbar(t("inbox_signature_saved"), { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || t("inbox_send_error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 3, height: "100%" }}>
      {/* Signature editor */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: 3, flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1rem" }}>
              {t("inbox_signature_title")}
            </Typography>
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: "0.75rem" }}>{isActive ? t("inbox_signature_active") : t("inbox_signature_inactive")}</Typography>}
            />
          </Box>

          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 1, display: "block" }}>
            {t("inbox_signature_help")}
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={12}
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            placeholder='<div style="font-family: Arial; font-size: 13px;">...'
            sx={{
              flex: 1, mb: 2,
              "& .MuiOutlinedInput-root": { fontSize: "0.8rem", fontFamily: "monospace" },
            }}
          />

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
            onClick={handleSave}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600, alignSelf: "flex-end" }}
          >
            {t("inbox_save")}
          </Button>
        </Paper>
      </Box>

      {/* Right side: Preview + Sync status */}
      <Box sx={{ width: 360, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Signature preview */}
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5, color: theme.palette.text.secondary }}>
            {t("inbox_signature_preview")}
          </Typography>
          {html ? (
            <Box
              sx={{
                p: 2, borderRadius: "8px",
                bgcolor: theme.palette.mode === "dark" ? "#1a1a1a" : "#fafafa",
                fontSize: "0.85rem",
                "& a": { color: theme.palette.primary.main },
              }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled, textAlign: "center", py: 2 }}>
              {t("inbox_signature_none")}
            </Typography>
          )}
        </Paper>

        {/* Sync status */}
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5, color: theme.palette.text.secondary }}>
            {t("inbox_sync_title")}
          </Typography>
          {syncStatus ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {syncStatus.sync_status === "idle" ? (
                  <Wifi size={14} color="#10b981" />
                ) : syncStatus.sync_status === "error" ? (
                  <WifiOff size={14} color="#ef4444" />
                ) : (
                  <RefreshCw size={14} color="#f59e0b" className="animate-spin" />
                )}
                <Chip
                  label={syncStatus.sync_status}
                  size="small"
                  sx={{
                    height: 20, fontSize: "0.65rem", fontWeight: 600, borderRadius: "6px",
                    bgcolor: syncStatus.sync_status === "idle"
                      ? alpha("#10b981", 0.1)
                      : syncStatus.sync_status === "error"
                        ? alpha("#ef4444", 0.1)
                        : alpha("#f59e0b", 0.1),
                    color: syncStatus.sync_status === "idle" ? "#10b981" : syncStatus.sync_status === "error" ? "#ef4444" : "#f59e0b",
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                Email: {syncStatus.user_email}
              </Typography>
              {syncStatus.last_sync_at && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                  {t("inbox_sync_last")}: {new Date(syncStatus.last_sync_at).toLocaleString()}
                </Typography>
              )}
              {syncStatus.error_message && (
                <Typography variant="caption" sx={{ color: "#ef4444", fontSize: "0.7rem" }}>
                  {t("inbox_sync_error")}: {syncStatus.error_message}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              Chargement...
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default EmailSettings;
